<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'inputtext-wrapper'"
        class="tl-input-wrapper"
    >
        <FloatLabel
            :id="inputId"
            :label="label"
        >
            <input
                :id="inputId"
                ref="input"
                :value="model"
                :type="options?.type || 'text'"
                :disabled="disabled"
                :maxlength="maxlength"
                :class="{
                    'tl-input': true,
                    'tl-input-filled': filled,
                    'tl-input-disabled': disabled,
                    'tl-input-focus': focused || parentFocused,
                    'tl-input-invalid': !!error,
                }"
                :aria-invalid="!!error || undefined"
                :placeholder="placeholder"
                @input="handleInput($event)"
                @focus="focused = true"
                @blur="focused = false"
                @keydown="handleKey"
                @keypress="handleKeyPress"
            />

            <div
                v-if="showCounter"
                class="tl-input-counter"
            >
                {{ model?.length ?? 0 }}
            </div>
        </FloatLabel>

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useId } from 'vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import { isNotNil } from 'ramda'
import FloatLabel from '../FloatLabel/FloatLabel.vue'
import { promiseTimeout } from '@vueuse/core'
import type { InputTextProps } from './types'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<InputTextProps & {
    placeholder?: string
    showCounter?: boolean
}>(), {
    showCounter: false,
})

const inputId = computed(() => {
    return props.id ? `${props.id}-input` : useId()
})

const UPPERCASE_VALIDATORS = ['ID_CARD_SERIES', 'ID_CARD_SERIES_AND_NUMBER']

const uppercase = computed(() => props.validator && UPPERCASE_VALIDATORS.includes(props.validator))

const emit = defineEmits(['update:modelValue', 'submit', 'mounted'])

const input = ref<HTMLInputElement>()

const model = defineModel<string>()

const filled = computed(() => !!model.value)
const focused = ref(false)
const label = computed(() => props.label)

const handleInput = (event: Event) => {
    const val = (event.target as HTMLInputElement).value

    model.value = uppercase.value
        ? val.toUpperCase()
        : val
}

onMounted(async () => {
    if (props.focus) {
        await promiseTimeout(400)
        input.value?.focus()
    }

    if (isNotNil(props.initialValue)) {
        model.value = props.initialValue
    }

    emit('mounted', input.value)
})

const handleKeyPress = (event: KeyboardEvent) => {
    if (props.validator && props.validator === 'ID_CARD_SERIES_AND_NUMBER') {
        if (!/^[a-zA-Z0-9_]*$/.test(event.key)) {
            event.preventDefault()
        }
    }
}

const handleKey = (event: KeyboardEvent) => {
    const code = event.code

    // ENTER
    if (code === 'Enter') {
        emit('submit')
    }
}
</script>

<style scoped lang="sass">
@import '../../style/components/input-base.sass'
</style>
